import { CollapsibleCard } from '@atgfr8/atg-ui-tailwind';
import { Input } from '@components/TextField/Input';
import {
  customerDetailsStore,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { isReadonly } from '@utils/utils';

const YesNoField = (props: {
  label: string;
  name: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
}) => (
  <fieldset class="min-w-0 max-w-full" data-testid={props.name}>
    <legend class="mb-1 max-w-full whitespace-normal text-sm font-medium text-armstrong-primary">
      {props.label}
    </legend>
    <div class="flex flex-wrap gap-x-4 gap-y-1">
      <label
        class={`flex min-w-0 items-center gap-1.5 text-sm text-armstrong-primary ${isReadonly() ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <input
          type="radio"
          name={props.name}
          class="shrink-0 cursor-pointer accent-armstrong-blue disabled:cursor-not-allowed"
          checked={props.value === true}
          disabled={isReadonly()}
          onChange={() => props.onChange(true)}
        />
        Yes
      </label>
      <label
        class={`flex min-w-0 items-center gap-1.5 text-sm text-armstrong-primary ${isReadonly() ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <input
          type="radio"
          name={props.name}
          class="shrink-0 cursor-pointer accent-armstrong-blue disabled:cursor-not-allowed"
          checked={props.value === false}
          disabled={isReadonly()}
          onChange={() => props.onChange(false)}
        />
        No
      </label>
    </div>
  </fieldset>
);

const AuthorityOption = (props: {
  label: string;
  name: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <label
    class={`flex min-w-0 items-center gap-2 whitespace-normal text-sm text-armstrong-primary ${isReadonly() ? 'cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <input
      type="checkbox"
      name={props.name}
      class="shrink-0 cursor-pointer accent-armstrong-blue disabled:cursor-not-allowed"
      checked={props.checked}
      disabled={isReadonly()}
      data-testid={props.name}
      onChange={(event) => props.onChange(event.currentTarget.checked)}
    />
    {props.label}
  </label>
);

const MC_NUMBER_MAX_LENGTH = 20;

const TransportationDetails = () => {
  const setMcNumber = (value: string) => {
    const cleaned = value
      .slice(0, MC_NUMBER_MAX_LENGTH)
      .replace(/\s+/g, ' ')
      .trim();
    updateCustomerDetailsState({ mcNumber: cleaned || null });
  };

  return (
    <CollapsibleCard
      startTitle="Transportation"
      class="mb-2 min-w-0 max-w-full"
    >
      <div class="grid w-full min-w-0 grid-cols-[minmax(0,13rem)_minmax(0,1fr)_minmax(0,1fr)] gap-x-4 gap-y-2">
        <div class="col-start-1 row-span-3 row-start-1 min-w-0 self-center [&>div]:my-0 [&_input]:cursor-pointer [&_input]:disabled:cursor-not-allowed">
          <Input
            id="transportation-mc-number"
            label="MC Number"
            value={customerDetailsStore.customer.mcNumber ?? ''}
            maxChar={MC_NUMBER_MAX_LENGTH}
            hideMaxCharCount
            disabled={isReadonly()}
            dataTestId="transportation-mc-number"
            onInput={(event) => {
              const value = event.target.value.slice(0, MC_NUMBER_MAX_LENGTH);
              updateCustomerDetailsState({
                mcNumber: value === '' ? null : value,
              });
            }}
            onChange={(event) => setMcNumber(event.target.value)}
          />
        </div>
        <p class="col-span-2 col-start-2 row-start-1 text-sm font-medium text-black">
          Authority
        </p>
        <div class="col-start-2 row-start-2 min-w-0">
          <AuthorityOption
            label="Freight Broker"
            name="isAuthorityFreightBroker"
            checked={
              customerDetailsStore.customer.isAuthorityFreightBroker === true
            }
            onChange={(checked) =>
              updateCustomerDetailsState({
                isAuthorityFreightBroker: checked,
              })
            }
          />
        </div>
        <div class="col-start-3 row-start-2 min-w-0">
          <AuthorityOption
            label="Carrier"
            name="isAuthorityCarrier"
            checked={customerDetailsStore.customer.isAuthorityCarrier === true}
            onChange={(checked) =>
              updateCustomerDetailsState({ isAuthorityCarrier: checked })
            }
          />
        </div>
        <div class="col-start-2 row-start-3 min-w-0">
          <AuthorityOption
            label="Freight Forwarder"
            name="isAuthorityFreightForwarder"
            checked={
              customerDetailsStore.customer.isAuthorityFreightForwarder === true
            }
            onChange={(checked) =>
              updateCustomerDetailsState({
                isAuthorityFreightForwarder: checked,
              })
            }
          />
        </div>
        <div class="col-start-3 row-start-3 min-w-0">
          <AuthorityOption
            label="Direct Shipper"
            name="isAuthorityDirectShipper"
            checked={
              customerDetailsStore.customer.isAuthorityDirectShipper === true
            }
            onChange={(checked) =>
              updateCustomerDetailsState({
                isAuthorityDirectShipper: checked,
              })
            }
          />
        </div>
        <div class="col-start-1 row-start-4 mt-1 min-w-0">
          <YesNoField
            label="Cross-Border Freight"
            name="isCrossBorder"
            value={customerDetailsStore.customer.isCrossBorder}
            onChange={(value) =>
              updateCustomerDetailsState({ isCrossBorder: value })
            }
          />
        </div>
        <div class="col-start-2 row-start-4 mt-1 min-w-0">
          <YesNoField
            label="Asset-Based"
            name="assetBased"
            value={customerDetailsStore.customer.assetBased}
            onChange={(value) =>
              updateCustomerDetailsState({ assetBased: value })
            }
          />
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default TransportationDetails;
